<template>
    <div class="single-file-uploader">
        <file-upload ref="upload" v-model="files" class="form-control single-file-input"
            :input-id="resolvedInputId" :name="resolvedInputId" :post-action="url" :accept="accept" :extensions="extensions"
            :size="maxSize" :maximum="1" :multiple="false" :disabled="disabled || busy"
            :drop="!disabled && !busy" :drop-directory="false"
            :chunk-enabled="true" :chunk="chunkOptions" :headers="headers"
            role="button" :tabindex="disabled || busy ? -1 : 0" :aria-disabled="disabled || busy ? 'true' : 'false'"
            @keydown.enter.native.prevent="openPicker" @keydown.space.native.prevent="openPicker"
            @input-filter="inputFilter" @input-file="inputFile">
            <span class="single-file-name">{{ selectedFile ? selectedFile.name : placeholder }}</span>
            <span class="single-file-browse">Bläddra</span>
        </file-upload>
        <small class="form-text text-muted">Dra och släpp en fil här eller klicka på Bläddra.</small>
        <div v-if="busy" class="progress mt-2" role="progressbar" :aria-valuenow="progress" aria-valuemin="0" aria-valuemax="100" aria-label="Uppladdning">
            <div class="progress-bar" :style="{ width: progress + '%' }">{{ progress }}%</div>
        </div>
        <small v-if="busy && progress === 100" class="form-text text-muted" role="status">Bearbetar filen…</small>
        <div v-if="errorMessage" class="text-danger mt-2" role="alert">{{ errorMessage }}</div>
    </div>
</template>

<script>
import FileUpload from 'vue-upload-component';

export default {
    name: 'SingleFileUploader',
    components: { FileUpload },
    props: {
        url: { type: String, required: true },
        inputId: { type: String, default: '' },
        accept: { type: String, default: '' },
        extensions: { type: String, default: '' },
        maxSize: { type: Number, default: 100 * 1024 * 1024 },
        disabled: { type: Boolean, default: false },
        placeholder: { type: String, default: 'Välj en fil…' },
        meta: { type: Object, default: () => ({}) },
    },
    data: () => ({
        files: [], busy: false, errorMessage: '', headers: {}, startBody: {}, pending: null,
    }),
    computed: {
        resolvedInputId() { return this.inputId || `single-file-${this._uid}`; },
        selectedFile() { return this.files[0] || null; },
        progress() { return Math.round(Number(this.selectedFile?.progress || 0)); },
        chunkOptions() {
            return {
                action: this.url, minSize: 0, maxActive: 1, maxRetries: 2, headers: this.headers, startBody: this.startBody,
            };
        },
    },
    created() {
        const token = document.head.querySelector('meta[name="csrf-token"]');
        this.headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        if (token) this.headers['X-CSRF-TOKEN'] = token.content;
    },
    beforeDestroy() {
        if (this.$refs.upload) this.$refs.upload.active = false;
        if (this.pending) this.pending.reject(new Error('Uppladdningen avbröts.'));
    },
    methods: {
        openPicker() {
            if (!this.disabled && !this.busy) document.getElementById(this.resolvedInputId)?.click();
        },
        inputFilter(newFile, oldFile, prevent) {
            if ((this.disabled || this.busy) && (!oldFile || !newFile || newFile.file !== oldFile.file)) prevent();
        },
        inputFile(newFile, oldFile) {
            if (!newFile || !oldFile || newFile.file !== oldFile.file) {
                this.errorMessage = '';
                this.$emit('input', newFile?.file || null);
            }
            if (newFile?.error && !oldFile?.error) {
                const response = newFile.response || {};
                const errors = response.errors ? Object.values(response.errors).flat().join(' ') : '';
                this.fail(errors || response.message || (newFile.error === 'size'
                    ? `Filen får vara högst ${Math.round(this.maxSize / 1024 / 1024)} MB.` : 'Filen kunde inte laddas upp. Försök igen.'));
            }
            if (newFile?.success && !oldFile?.success) {
                this.busy = false;
                this.$emit('busy', false);
                this.$emit('success', newFile.response);
                const pending = this.pending;
                this.pending = null;
                if (pending) pending.resolve(newFile.response);
            }
        },
        fail(message) {
            this.errorMessage = message;
            this.busy = false;
            this.$emit('busy', false);
            const pending = this.pending;
            this.pending = null;
            if (pending) pending.reject(new Error(message));
        },
        validateSelection() {
            let message = '';
            if (!this.selectedFile) message = 'Välj en fil först.';
            else if (this.selectedFile.size > this.maxSize || this.selectedFile.size === 0) {
                message = `Välj en fil som är större än 0 byte och högst ${Math.round(this.maxSize / 1024 / 1024)} MB.`;
            } else if (this.extensions && !this.extensions.split(',').includes(this.selectedFile.name.split('.').pop().toLowerCase())) {
                message = 'Filtypen är inte tillåten.';
            }
            if (message) {
                this.errorMessage = message;
                throw new Error(message);
            }
        },
        async upload(meta = {}) {
            if (this.busy) throw new Error('En uppladdning pågår redan.');
            this.validateSelection();
            this.errorMessage = '';
            this.startBody = { ...this.meta, ...meta };
            // A failed attempt starts a fresh server session when the user retries.
            this.$refs.upload.update(this.selectedFile, {
                error: '', success: false, progress: '0.00', chunk: null, response: {},
            });
            this.busy = true;
            this.$emit('busy', true);
            await this.$nextTick();
            return new Promise((resolve, reject) => {
                this.pending = { resolve, reject };
                this.$refs.upload.active = true;
            });
        },
        clear() {
            if (!this.busy) this.$refs.upload.clear();
        },
    },
};
</script>

<style scoped>
.single-file-input.file-uploads {
    display: flex;
    align-items: center;
    padding: 0;
    text-align: left;
}
.single-file-input:focus { outline: 2px solid #2196f3; outline-offset: 2px; }
.single-file-name { flex: 1; padding: .4375rem .875rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.single-file-browse { align-self: stretch; display: flex; align-items: center; padding: .4375rem .875rem; background: #f5f5f5; border-left: 1px solid #ddd; }
.single-file-input.file-uploads-disabled { background: #eee; }
.single-file-input.file-uploads-disabled >>> label { pointer-events: none; }
</style>
